import Icon from 'components/Icon';
import {TServerError} from 'core/types';
import {ANIMATION_LATENCY, NOTHING_FOUND_ERROR_CODE} from 'modules/search-popup/constants';
import {ISearchData, ISearchLink} from 'modules/search-popup/models';
import Link from 'components/CustomRoutingLink';
import React, {FC, memo, useEffect, useRef, useState} from 'react';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';
import style from './style.module.scss';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {getSearchPageLink} from 'modules/search-page-new/helpers';
import {SearchProductCard} from 'modules/search-popup/components/SearchProductCard';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';
import {useSearchGtagEvents} from 'hooks/analytics/gtag/search/useSearchGtagEvents';
import {EventListName} from 'modules/google-analytics/constants';

interface IProps {
    isVisible: boolean;
    isLoading: boolean;
    errors?: TServerError[];
    searchValue?: string;
    suggestions?: (string | ISearchLink)[];
    recommendedProducts?: ISearchData['products'];
}

function escapeRegExp(string: string) {
    // $& - полностью совпадающая строка
    return string.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

function getHighlightedText(text: string, highlight: string) {
    const cleanHighlight = highlight.replace(/\s/gu, '');

    if (!cleanHighlight) {
        return text;
    }

    const regex = new RegExp(escapeRegExp(cleanHighlight), 'i');
    const match = regex.exec(text);

    if (!match) {
        return text;
    }

    const start = match.index;
    const end = start + match[0].length;

    return (
        <>
            {text.slice(0, start)}
            <span className={style.highLightLetter}>{text.slice(start, end)}</span>
            {text.slice(end)}
        </>
    );
}

const Recommendations: FC<{
    products: ISearchData['products'];
    searchTerm?: string;
    onProductClick: (product: ISearchData['products'][number]) => void;
}> = ({products, searchTerm, onProductClick}) => (
    <div className={style.searchRecommendations}>
        {products?.map((product, index) => (
            <SearchProductCard
                key={product.offerId}
                product={product}
                analytics={{
                    positionIndex: index + 1,
                    searchTerm,
                    listName: EventListName.SearchRecommendations,
                }}
                onProductClick={() => onProductClick(product)}
            />
        ))}
    </div>
);

const History: FC<{
    history: string[];
    onClear: () => void;
    onRemove: (value: string) => () => void;
}> = ({history, onClear, onRemove}) => {
    const {handleHistoryClickGtag} = useSearchGtagEvents();
    return (
        <>
            <div className={style.historyContainer}>
                <div className={style.historyHeader}>
                    <Typography variants={{DT: 'title-20-m', TB: 'title-20-m', MB: 'body-18-m'}}>История</Typography>
                    <button className={style.clearHistoryBtn} onClick={onClear}>
                        <Typography variant={'p'}>очистить</Typography>
                    </button>
                </div>
                <ul className={style.historyList}>
                    {history.map((item, index) => {
                        return (
                            <li className={style.historyItem} key={item}>
                                <Link
                                    className={style.historyItemLink}
                                    href={getSearchPageLink(item)}
                                    onClick={() => handleHistoryClickGtag(item, index)}
                                >
                                    <Typography variant={'p'}>{item}</Typography>
                                </Link>
                                <button className={style.removeBtn} onClick={onRemove(item)}>
                                    <Icon icon={'close-m'} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
};

// eslint-disable-next-line complexity
export const SearchPanel: FC<IProps> = memo(function SearchPanel({
    isVisible,
    isLoading,
    errors,
    searchValue,
    suggestions = [],
    recommendedProducts = [],
}) {
    const {handleSuggestionClickGtag, handleSuggestProductClickGtag} = useSearchGtagEvents();

    const [componentMounted, setComponentMounted] = useState(false);
    const [visibleStyle, setVisibleStyle] = useState(false);

    const timeoutRef = useRef<NodeJS.Timeout>();

    const notFoundError = errors?.some(({code}) => NOTHING_FOUND_ERROR_CODE === code);

    const [searchHistory, setSearchHistory] = useState<string[]>(() => {
        if ('undefined' === typeof window) {
            return [];
        }
        const raw = localStorage.getItem('recently');
        if (!raw) {
            return [];
        }
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.every((item) => 'string' === typeof item)) {
                return parsed;
            }
            return [];
        } catch {
            localStorage.removeItem('recently');
            return [];
        }
    });

    useEffect(() => {
        if (isVisible) {
            setComponentMounted(true);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setVisibleStyle(true), 0);
        } else {
            setVisibleStyle(false);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setComponentMounted(false), ANIMATION_LATENCY);
        }
    }, [isVisible]);

    useEffect(() => {
        if (notFoundError) {
            setVisibleStyle(false);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setComponentMounted(false), ANIMATION_LATENCY);
        }
    }, [notFoundError]);

    const handleClearHistory = () => {
        localStorage.removeItem('recently');
        setSearchHistory([]);
    };

    const handleRemoveHistoryItem = (value: string) => () => {
        if (1 === searchHistory.length) {
            handleClearHistory();
            return;
        }
        const updatedSearchValues = searchHistory.filter((item) => value !== item);
        localStorage.setItem('recently', JSON.stringify(updatedSearchValues));
        setSearchHistory(updatedSearchValues);
    };

    if (!componentMounted) {
        return null;
    }

    const isEmpty = 0 === suggestions.length && 0 === recommendedProducts.length && 0 === searchHistory.length;

    return (
        <SkeletonWrapper
            className={classNames(style.container, 'result-suggestions__link')}
            dataAutotestTarget={'header-search'}
            dataAutotestTargetId={'header-search-history'}
            disabled={!isLoading}
            fadeInEffect={true}
        >
            <div
                className={classNames(style.panel, {
                    [style.panelHidden]: !visibleStyle || isEmpty,
                    [style.panelVisible]: visibleStyle && !isEmpty,
                })}
            >
                {searchValue && 0 < suggestions.length ? (
                    <>
                        {0 < suggestions.length && (
                            <ul className={style.suggestionsList}>
                                {suggestions.map((suggestion, index) => {
                                    const isLink = 'object' === typeof suggestion && 'url' in suggestion;
                                    const suggestionValue = !isLink ? suggestion : suggestion.name;
                                    const href = withRedirectParam(
                                        !isLink ? getSearchPageLink(suggestion) : suggestion.url,
                                    );

                                    return (
                                        <li key={!isLink ? suggestion : suggestion.url}>
                                            <Link
                                                className={style.suggestionItem}
                                                href={href}
                                                onClick={() =>
                                                    handleSuggestionClickGtag(
                                                        isLink ? suggestion.name : suggestion,
                                                        index,
                                                    )
                                                }
                                            >
                                                <Typography variant={'p'}>
                                                    {getHighlightedText(suggestionValue, searchValue)}
                                                </Typography>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {0 < recommendedProducts.length && (
                            <Recommendations
                                products={recommendedProducts}
                                searchTerm={searchValue}
                                onProductClick={(product) => handleSuggestProductClickGtag(product, searchValue)}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {0 < searchHistory.length && (
                            <History
                                history={searchHistory}
                                onClear={handleClearHistory}
                                onRemove={handleRemoveHistoryItem}
                            />
                        )}
                        {0 < recommendedProducts.length && (
                            <>
                                <Typography
                                    element={'div'}
                                    variants={{
                                        DT: 'title-20-m',
                                        TB: 'title-20-m',
                                        MB: 'body-18-m',
                                    }}
                                    className={style.recommendationsTitle}
                                >
                                    Рекомендуем
                                </Typography>
                                <Recommendations
                                    products={recommendedProducts}
                                    onProductClick={(product) => handleSuggestProductClickGtag(product, searchValue)}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </SkeletonWrapper>
    );
});
